import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ShoppingCart, RefreshCcw, PackageCheck, Trash2, CheckCircle2, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const SOURCE_LABEL = {
  manual: { label: "Manual", cls: "bg-slate-100 text-slate-600" },
  low_stock: { label: "Low Stock", cls: "bg-amber-50 text-amber-700" },
  mrp: { label: "MRP", cls: "bg-blue-50 text-blue-700" },
  mrp_auto: { label: "Auto MRP", cls: "bg-indigo-50 text-indigo-700" },
};
const sourceMeta = (s) => SOURCE_LABEL[s] || SOURCE_LABEL.manual;

const STATUS_LABEL = {
  draft: "bg-slate-100 text-slate-600",
  approved: "bg-blue-50 text-blue-700",
  ordered: "bg-amber-50 text-amber-700",
  received: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

export default function PurchaseRequestsPage() {
  const { user } = useAuth();
  const [prs, setPrs] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const role = user?.role;
  const canApprove = ["admin", "planner"].includes(role);
  const canOrder = ["admin", "planner"].includes(role);
  const canReceive = ["admin", "planner", "operator"].includes(role);
  const canCancel = role === "admin";

  const load = async () => {
    const [p, i] = await Promise.all([api.get("/purchase-requests"), api.get("/items")]);
    setPrs(p.data);
    setItems(i.data);
    if (selected) {
      const found = p.data.find((x) => x.id === selected.id);
      setSelected(found || null);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));

  const autoLowStock = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/purchase-requests/auto-low-stock");
      if (data.purchase_request) {
        toast.success(data.message);
        await load();
      } else {
        toast.info(data.message);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Auto-generation failed");
    } finally { setBusy(false); }
  };

  const callTransition = async (pr, action, successMsg) => {
    setBusy(true);
    try {
      await api.post(`/purchase-requests/${pr.id}/${action}`);
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Transition failed");
    } finally { setBusy(false); }
  };

  const remove = async (pr) => {
    if (!window.confirm(`Delete ${pr.code}?`)) return;
    await api.delete(`/purchase-requests/${pr.id}`);
    if (selected?.id === pr.id) setSelected(null);
    load();
  };

  const total = (pr) => (pr.lines || []).reduce((s, l) => s + (l.estimated_cost || 0), 0);

  const nextAction = (pr) => {
    if (pr.status === "draft" && canApprove) return { key: "approve", label: "Approve", icon: CheckCircle2, msg: `${pr.code} approved` };
    if (pr.status === "approved" && canOrder) return { key: "order", label: "Mark Ordered", icon: Send, msg: `${pr.code} → ordered (PO created)` };
    if (pr.status === "ordered" && canReceive) return { key: "receive", label: "Mark Received", icon: PackageCheck, msg: `${pr.code} received, stock credited` };
    return null;
  };

  return (
    <div className="space-y-6" data-testid="pr-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Procurement</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Purchase Requests
          </h1>
          <p className="text-sm text-slate-500 mt-1">Approval workflow · auto-generated on MOQ + low-stock scans and MRP planned purchases.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-pr-btn">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={autoLowStock} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="auto-low-stock-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> Auto-generate from Low Stock
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Requests</span>
            <span className="text-xs text-slate-500 mono">{prs.length}</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {prs.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-prs">
                No purchase requests yet.
              </div>
            )}
            {prs.map((pr) => (
              <button
                key={pr.id}
                onClick={() => setSelected(pr)}
                data-testid={`pr-row-${pr.code}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === pr.id ? "bg-blue-50/60" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{pr.code}</div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_LABEL[pr.status]}`}>{pr.status}</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full tracking-wider ${sourceMeta(pr.source).cls}`}>
                    {sourceMeta(pr.source).label}
                  </span>
                  <span className="text-xs text-slate-500">{pr.lines.length} line(s)</span>
                  {pr.procurement_class && (
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full tracking-wider bg-slate-100 text-slate-600" data-testid={`pr-class-${pr.code}`}>
                      {pr.procurement_class === "regular" ? "Regular" : "Non-Regular"}
                    </span>
                  )}
                  {pr.purchase_team_status === "pending" && (
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full tracking-wider bg-amber-50 text-amber-700" data-testid={`pr-team-${pr.code}`}>
                      Purchase Team
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-slate-600 mono">₹ {total(pr).toLocaleString()}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="pr-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <ShoppingCart className="w-10 h-10 text-slate-300" />
              Select a purchase request to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    Purchase Request
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    {sourceMeta(selected.source).label} · created by {selected.created_by || "—"}
                    {selected.approved_by ? <> · approved by <span className="font-medium text-slate-700">{selected.approved_by}</span></> : null}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_LABEL[selected.status]}`}>{selected.status}</span>
              </div>

              {selected.source === "mrp_auto" && (
                <div className="text-xs rounded-md border border-indigo-100 bg-indigo-50/60 text-indigo-900 px-3 py-2" data-testid="pr-auto-mrp-note">
                  Raised by the automated MRP run
                  {selected.procurement_class ? ` · ${selected.procurement_class === "regular" ? "Regular" : "Non-Regular"} items` : ""}
                  {selected.required_date ? ` · required by ${selected.required_date}` : ""}
                  {selected.routing_reason ? <div className="mt-1 text-amber-800">Routed to Purchase Team: {selected.routing_reason}</div> : null}
                </div>
              )}

              {/* Transition controls */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="pr-actions">
                {nextAction(selected) ? (
                  <Button
                    onClick={() => callTransition(selected, nextAction(selected).key, nextAction(selected).msg)}
                    disabled={busy}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid={`pr-${nextAction(selected).key}-btn`}
                  >
                    {(() => { const I = nextAction(selected).icon; return <I className="w-4 h-4 mr-1.5" />; })()}
                    {nextAction(selected).label}
                  </Button>
                ) : selected.status === "received" ? (
                  <span className="text-xs text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Workflow complete — stock credited on {selected.received_at?.slice(0, 10)}.
                  </span>
                ) : selected.status === "cancelled" ? (
                  <span className="text-xs text-red-700">Cancelled by {selected.cancelled_by} on {selected.cancelled_at?.slice(0, 10)}.</span>
                ) : (
                  <span className="text-xs text-slate-500">No action available for your role ({role}).</span>
                )}
                {canCancel && !["received", "cancelled"].includes(selected.status) && (
                  <Button variant="outline" onClick={() => callTransition(selected, "cancel", `${selected.code} cancelled`)} disabled={busy} data-testid="pr-cancel-btn">
                    <XCircle className="w-4 h-4 mr-1.5 text-red-500" /> Cancel
                  </Button>
                )}
                {canCancel && selected.status === "draft" && (
                  <Button variant="ghost" size="icon" onClick={() => remove(selected)} className="ml-auto" data-testid="delete-pr-btn">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic">{selected.notes}</div>
              )}

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Lines</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                      <th className="py-2 px-3">Code</th><th className="py-2 px-3">Name</th>
                      <th className="py-2 px-3">Qty</th><th className="py-2 px-3">Supplier</th>
                      <th className="py-2 px-3">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.lines.map((ln, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="py-2 px-3 mono">{itemMap[ln.item_id]?.code || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{itemMap[ln.item_id]?.name || "—"}</td>
                        <td className="py-2 px-3 mono font-semibold">{ln.qty}</td>
                        <td className="py-2 px-3 text-slate-600">{ln.supplier || "—"}</td>
                        <td className="py-2 px-3 mono">₹ {Number(ln.estimated_cost || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200">
                      <td colSpan={4} className="py-2 px-3 text-right text-xs uppercase tracking-[0.14em] text-slate-500">Estimated Total</td>
                      <td className="py-2 px-3 mono font-bold text-slate-900">₹ {total(selected).toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="text-xs text-slate-500 flex items-center gap-1">
                <PackageCheck className="w-3.5 h-3.5" />
                <span>Workflow: <span className="mono">draft → approved → ordered → received</span>. Marking <span className="font-semibold">ordered</span> creates a Purchase Order; <span className="font-semibold">received</span> auto-credits stock.</span>
              </div>

              {(selected.lines || []).map((ln, idx) => ln.reason && (
                <div key={`r-${idx}`} className="text-xs text-slate-500">
                  <span className="mono font-semibold text-slate-700">{itemMap[ln.item_id]?.code}:</span> {ln.reason}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
